import { useEffect, useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, Search } from "lucide-react";
import { StepTitle, NextButton } from "./fields";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const formatPlate = (raw) => {
  const c = (raw || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 7);
  return [c.slice(0, 2), c.slice(2, 5), c.slice(5, 7)].filter(Boolean).join("-");
};

const FIELDS = [
  ["marque", "Marque"], ["modele", "Modèle"], ["version", "Version"], ["energie", "Énergie"],
  ["puissance_fiscale", "Puissance fiscale (CV)"], ["date_mise_circulation", "1re mise en circulation"],
];

const VehicleSummary = ({ v, moto }) => (
  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-4 text-sm grid grid-cols-2 gap-2" data-testid="calc-vehicle-summary">
    {[...FIELDS, ...(moto ? [["cylindree", "Cylindrée"]] : [])].map(([k, l]) => (
      <div key={k}><span className="text-gray-500">{l} : </span><span className="font-medium text-gray-900">{v[k] || "—"}</span></div>
    ))}
  </div>
);

const ManualForm = ({ moto, initial, onNext }) => {
  const [v, setV] = useState(initial);
  const keys = [...FIELDS, ...(moto ? [["cylindree", "Cylindrée (cm³)"]] : [])];
  return (
    <div className="space-y-3" data-testid="calc-vehicle-manual">
      {keys.map(([k, l]) => (
        <div key={k}>
          <Label className="text-sm mb-1 block">{l}</Label>
          <Input value={v[k] || ""} onChange={(e) => setV({ ...v, [k]: e.target.value })} className="h-11" data-testid={`calc-vehicle-${k}`} />
        </div>
      ))}
      <NextButton disabled={!v.marque || !v.modele} onClick={() => onNext({ ...v, saisie_manuelle: true })} />
    </div>
  );
};

export const PlateStep = ({ moto, answers, next, initialPlate }) => {
  const [plate, setPlate] = useState(formatPlate(answers.plaque || initialPlate));
  const [loading, setLoading] = useState(false);
  const [vehicle, setVehicle] = useState(null);
  const [manual, setManual] = useState(false);

  const lookup = async (p = plate) => {
    if (p.replace(/-/g, "").length !== 7) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/calc/vehicle/${p}`);
      if (res.data.found) setVehicle(res.data);
      else setManual(true);
    } catch (e) {
      setManual(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (initialPlate && !answers.marque) lookup(formatPlate(initialPlate));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finish = (v) => next({ plaque: plate, marque: v.marque, modele: v.modele, version: v.version, energie: v.energie,
    puissance_fiscale: v.puissance_fiscale, puissance_ch: v.puissance_ch, date_mise_circulation: v.date_mise_circulation,
    ...(moto ? { cylindree: v.cylindree } : {}), saisie_manuelle: !!v.saisie_manuelle,
    ...(v.saisie_manuelle ? {} : { sra_id: v.sra_id || null, sra_group: v.sra_group || null }) });

  return (
    <>
      <StepTitle title={`Quelle est la plaque de votre ${moto ? "moto" : "voiture"} ?`} subtitle="Remplissage automatique de votre véhicule" />
      {!vehicle && !manual && (
        <div className="flex gap-2">
          <Input placeholder="AB-123-CD" value={plate} onChange={(e) => setPlate(formatPlate(e.target.value))}
            onKeyDown={(e) => e.key === "Enter" && lookup()} className="text-center text-lg font-mono tracking-wider h-12" data-testid="calc-plate-input" />
          <Button onClick={() => lookup()} disabled={loading || plate.length !== 9} className="h-12 px-6 bg-[#102692]" data-testid="calc-plate-search-btn">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
          </Button>
        </div>
      )}
      {!vehicle && !manual && (
        <button onClick={() => setManual(true)} className="mt-3 text-sm text-[#102692] underline" data-testid="calc-plate-manual-link">
          Je n'ai pas la plaque / saisie manuelle
        </button>
      )}
      {vehicle && !manual && (
        <>
          <VehicleSummary v={vehicle} moto={moto} />
          <p className="text-center font-semibold text-gray-900 mt-5 mb-3">Est-ce bien votre véhicule ?</p>
          <div className="grid grid-cols-2 gap-3">
            <Button onClick={() => finish(vehicle)} className="h-12 bg-[#102692]" data-testid="calc-vehicle-yes-btn">Oui</Button>
            <Button variant="outline" onClick={() => setManual(true)} className="h-12" data-testid="calc-vehicle-no-btn">Non</Button>
          </div>
        </>
      )}
      {manual && <ManualForm moto={moto} initial={vehicle || {}} onNext={finish} />}
    </>
  );
};
